import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, X, Ticket, ShoppingCart, ShoppingBag, Megaphone, Briefcase,
  User, ChevronDown, Heart, Users as UsersIcon, Trophy, UserCircle2,
  Newspaper, CalendarDays, GraduationCap, UserPlus, School, Sparkles,
  History, Network, Building2, MapPin, Mail, Phone, FileText, HandHeart,
  Handshake, Tag, Megaphone as MegaphoneIcon, Shirt,
} from "lucide-react";
import { useCart } from "../../lib/useCart";
import { useCustomerAuth } from "../../lib/useCustomerAuth";
import NotificationsBell from "./NotificationsBell";

const MENU_BG = "https://customer-assets.emergentagent.com/job_pod-istres-hb/artifacts/lxan897l_back-menu-iphb.png";

// ============================================================
// MEGA MENU CONTENT — same structure as the reference site
// ============================================================
const MEGA_COLUMNS_TOP = [
  {
    title: "Équipe Professionnelle",
    color: "white",
    icon: Trophy,
    items: [
      { label: "Actualités", to: "/sport/actualites", icon: Newspaper },
      { label: "Effectif", to: "/sport/effectif", icon: UsersIcon },
      { label: "Calendrier", to: "/sport/calendrier", icon: CalendarDays },
      { label: "Classement", to: "/sport/classement", icon: Trophy },
    ],
  },
  {
    title: "Centre de Formation",
    color: "white",
    icon: GraduationCap,
    items: [
      { label: "Le centre de formation", to: "/sport/club/centre-formation", icon: GraduationCap },
      { label: "L'équipe Réserve", to: "/sport/club/reserve", icon: UsersIcon },
      { label: "Calendrier", to: "/sport/club/centre-formation#calendrier", icon: CalendarDays },
      { label: "Classement", to: "/sport/club/centre-formation#classement", icon: Trophy },
      { label: "Joueurs issus de la formation", to: "/sport/club/joueurs-formation", icon: Sparkles },
    ],
  },
  {
    title: "Féminines",
    color: "white",
    icon: Heart,
    items: [
      { label: "Équipe première féminine", to: "/sport/club/feminines", icon: UsersIcon },
      { label: "Calendrier féminine", to: "/sport/club/feminines#calendrier", icon: CalendarDays },
      { label: "Classement féminine", to: "/sport/club/feminines#classement", icon: Trophy },
    ],
  },
  {
    title: "Secteur Amateur",
    color: "white",
    icon: HandHeart,
    items: [
      { label: "Actualités", to: "/sport/actualites?cat=amateur", icon: Newspaper },
      { label: "Les équipes", to: "/sport/club/les-equipes", icon: UsersIcon },
      { label: "Rejoindre une équipe", to: "/sport/rejoindre", icon: UserPlus },
      { label: "Section Sportive lycée Arthur Rimbaud", to: "/sport/club/section-sportive", icon: School },
      { label: "Académie « Istres Terre de Handball »", to: "/sport/club/academie", icon: GraduationCap },
    ],
  },
];

const MEGA_COLUMNS_BOTTOM = [
  {
    title: "Le Club",
    color: "pink",
    icon: Sparkles,
    items: [
      { label: "L'histoire du club", to: "/sport/club/histoire", icon: History },
      { label: "Organigramme", to: "/sport/club/organigramme", icon: Network },
    ],
  },
  {
    title: "Infos Pratiques",
    color: "white",
    icon: MapPin,
    items: [
      { label: "Accès à la Halle Polyvalente", to: "/sport/club/salle", icon: MapPin },
      { label: "Soir de match", to: "/sport/club/soir-de-match", icon: Ticket },
      { label: "Devenez bénévole", to: "/sport/club/benevole", icon: HandHeart },
      { label: "Presse & Médias", to: "/sport/club/presse-medias", icon: FileText },
      { label: "Contact", to: "/sport/contact", icon: Mail },
    ],
  },
  {
    title: "Business",
    color: "yellow",
    icon: Briefcase,
    items: [
      { label: "Actualité", to: "/sport/actualites?cat=partenaires", icon: MegaphoneIcon },
      { label: "Prestations", to: "/sport/partenaires#prestations", icon: Tag },
      { label: "Partenaires", to: "/sport/partenaires", icon: Handshake },
      { label: "Contact", to: "/sport/partenaires#contact", icon: Phone },
    ],
  },
];

// ============================================================
// MEGA MENU OVERLAY
// ============================================================
function MegaMenu({ open, onClose, primary, secondary }) {
  const colorClasses = {
    white: "text-white",
    pink: "text-pink-400",
    yellow: "text-amber-300",
  };
  const underlineColor = {
    white: secondary,
    pink: secondary,
    yellow: "#FCD34D",
  };

  // Generate animated pink dots
  const dots = React.useMemo(() => Array.from({ length: 28 }).map((_, i) => ({
    left: `${(i * 37) % 100}%`,
    top: `${((i * 53) % 100)}%`,
    size: 2 + ((i * 7) % 4),
    delay: (i % 8) * 0.4,
    duration: 4 + (i % 5),
  })), []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="mega-menu-overlay"
          className="fixed inset-0 z-[60] overflow-y-auto"
          style={{
            background: `linear-gradient(160deg, #6B2D5C 0%, #4F1B7C 55%, #2a0e2c 100%)`,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          }}
        >
          {/* Subtle background image (very low opacity) */}
          <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{ backgroundImage: `url(${MENU_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}/>

          {/* Halftone radial accents */}
          <div
            className="absolute inset-0 opacity-25 pointer-events-none"
            style={{
              backgroundImage: `radial-gradient(circle at 90% 10%, ${secondary}55, transparent 35%), radial-gradient(circle at 10% 80%, ${secondary}55, transparent 35%)`,
            }}
          />
          <div
            className="absolute right-0 top-32 w-[45%] h-[45%] opacity-25 pointer-events-none"
            style={{
              backgroundImage: `radial-gradient(circle, ${secondary} 1px, transparent 1px)`,
              backgroundSize: "14px 14px",
              maskImage: "radial-gradient(ellipse at center, black, transparent 70%)",
            }}
          />
          <div
            className="absolute left-0 bottom-24 w-[45%] h-[45%] opacity-25 pointer-events-none"
            style={{
              backgroundImage: `radial-gradient(circle, ${secondary} 1px, transparent 1px)`,
              backgroundSize: "14px 14px",
              maskImage: "radial-gradient(ellipse at center, black, transparent 70%)",
            }}
          />

          {/* Animated floating pink dots */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {dots.map((d, i) => (
              <motion.span key={i}
                className="absolute rounded-full"
                style={{
                  left: d.left, top: d.top,
                  width: d.size, height: d.size,
                  background: secondary,
                  boxShadow: `0 0 ${d.size * 2}px ${secondary}`,
                }}
                animate={{
                  opacity: [0, 0.85, 0],
                  scale: [0.5, 1.4, 0.5],
                  y: [0, -22, 0],
                }}
                transition={{
                  duration: d.duration,
                  delay: d.delay,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-10 min-h-screen">
            {/* Top bar: Close button (left) + Quick icons (center) + Club logo (right) */}
            <div className="flex items-center justify-between gap-3">
              <motion.button
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                onClick={onClose}
                data-testid="mega-menu-close"
                className="text-white hover:text-pink-300 transition shrink-0"
                aria-label="Fermer le menu"
              >
                <X size={36} strokeWidth={2.5}/>
              </motion.button>

              {/* Quick icons cluster (center) */}
              <div className="flex items-center gap-2.5 sm:gap-4">
                {[
                  { to: "/sport/boutique", label: "Boutique", icon: ShoppingBag, color: secondary, testid: "mega-quick-boutique" },
                  { to: "/sport/medias",  label: "Actu",     icon: Megaphone,   color: secondary, testid: "mega-quick-actu" },
                  { to: "/sport/mon-espace",label: "Compte",   icon: UserCircle2, color: secondary, testid: "mega-quick-compte" },
                ].map((q, i) => (
                  <motion.div
                    key={q.label}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.18 + i * 0.07 }}
                  >
                    <Link
                      to={q.to}
                      onClick={onClose}
                      data-testid={q.testid}
                      className="group flex flex-col items-center gap-1 text-white"
                      aria-label={q.label}
                    >
                      <span className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 border border-white/15 group-hover:bg-pink-500/40 group-hover:border-pink-400 backdrop-blur flex items-center justify-center transition shadow-md">
                        <q.icon size={18} className="text-pink-200 group-hover:text-white transition" strokeWidth={2.4}/>
                      </span>
                      <span className="text-[9px] sm:text-[10px] font-black tracking-widest uppercase opacity-80 group-hover:opacity-100 group-hover:text-pink-200 transition">{q.label}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.15, type: "spring", stiffness: 220, damping: 18 }}
                className="shrink-0"
                data-testid="mega-menu-club-logo"
              >
                <img
                  src="https://lnh.fr/medias/sports_teams/istres__logo__2023-2024.png"
                  alt="Istres Provence Handball"
                  className="w-[72px] h-[72px] sm:w-[84px] sm:h-[84px] object-contain drop-shadow-[0_4px_18px_rgba(236,31,124,0.5)]"
                />
              </motion.div>
            </div>

            {/* TOP ROW (4 cols) — masonry on mobile (columns flow) to avoid empty gaps */}
            <div className="mt-4 sm:mt-8 columns-2 gap-x-4 sm:columns-1 md:columns-4 [&>*]:break-inside-avoid [&>*]:mb-4 sm:[&>*]:mb-0 sm:grid sm:grid-cols-2 md:grid-cols-4 sm:gap-x-6 sm:gap-y-4 lg:gap-x-10">
              {MEGA_COLUMNS_TOP.map((col, ci) => {
                const ColIcon = col.icon;
                return (
                  <motion.div
                    key={col.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + ci * 0.06 }}
                  >
                    <h3 className={`flex items-center gap-2 font-black uppercase tracking-wider text-xs sm:text-sm ${colorClasses[col.color]}`}>
                      {ColIcon && <ColIcon size={14} className="opacity-90"/>}
                      {col.title}
                    </h3>
                    <div className="mt-1.5 h-0.5 w-10 sm:w-12" style={{ background: underlineColor[col.color] }}/>
                    <ul className="mt-3 space-y-0.5">
                      {col.items.map((it, ii) => {
                        const ItIcon = it.icon;
                        return (
                          <motion.li
                            key={it.label}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.25 + ci * 0.06 + ii * 0.03 }}
                          >
                            <Link
                              to={it.to}
                              onClick={onClose}
                              data-testid={`mega-link-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
                              className="group flex items-center gap-2 sm:gap-2.5 text-white/90 hover:text-pink-300 hover:bg-white/5 transition px-2 py-0.5 -mx-2 rounded-lg text-[12px] sm:text-[13px] leading-snug"
                            >
                              {ItIcon && (
                                <span className="shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white/8 group-hover:bg-pink-500/30 flex items-center justify-center transition">
                                  <ItIcon size={11} className="opacity-95 sm:hidden"/>
                                  <ItIcon size={12} className="opacity-95 hidden sm:inline"/>
                                </span>
                              )}
                              <span>{it.label}</span>
                            </Link>
                          </motion.li>
                        );
                      })}
                    </ul>
                  </motion.div>
                );
              })}
            </div>

            {/* BOTTOM ROW (3 cols) — masonry on mobile (columns flow) to avoid empty gaps */}
            <div className="mt-2 sm:mt-6 columns-2 gap-x-4 sm:columns-1 md:columns-4 [&>*]:break-inside-avoid [&>*]:mb-4 sm:[&>*]:mb-0 sm:grid sm:grid-cols-2 md:grid-cols-4 sm:gap-x-6 sm:gap-y-3 lg:gap-x-10">
              {MEGA_COLUMNS_BOTTOM.map((col, ci) => {
                const ColIcon = col.icon;
                return (
                  <motion.div
                    key={col.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45 + ci * 0.06 }}
                    className={ci === 0 ? "" : ci === 1 ? "md:col-start-2" : ""}
                  >
                    <h3 className={`flex items-center gap-2 font-black uppercase tracking-wider text-xs sm:text-sm ${colorClasses[col.color]}`}>
                      {ColIcon && <ColIcon size={14} className="opacity-90"/>}
                      {col.title}
                    </h3>
                    <div className="mt-1.5 h-0.5 w-10 sm:w-12" style={{ background: underlineColor[col.color] }}/>
                    <ul className="mt-3 space-y-0.5">
                      {col.items.map((it, ii) => {
                        const ItIcon = it.icon;
                        return (
                          <motion.li
                            key={it.label}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.55 + ci * 0.06 + ii * 0.03 }}
                          >
                            <Link
                              to={it.to}
                              onClick={onClose}
                              className="group flex items-center gap-2 sm:gap-2.5 text-white/90 hover:text-pink-300 hover:bg-white/5 transition px-2 py-0.5 -mx-2 rounded-lg text-[12px] sm:text-[13px] leading-snug"
                            >
                              {ItIcon && (
                                <span className="shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white/8 group-hover:bg-pink-500/30 flex items-center justify-center transition">
                                  <ItIcon size={11} className="opacity-95 sm:hidden"/>
                                  <ItIcon size={12} className="opacity-95 hidden sm:inline"/>
                                </span>
                              )}
                              <span>{it.label}</span>
                            </Link>
                          </motion.li>
                        );
                      })}
                    </ul>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============================================================
// HEADER (top bar)
// ============================================================
export default function SportHeader({ settings = {} }) {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const loc = useLocation();
  const { count, setDrawerOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMegaOpen(false);
  }, [loc.pathname]);

  const primary = settings?.primary_color || "#4F1B7C";
  const secondary = settings?.secondary_color || "#EC1F7C";
  const logo = settings?.logo_url || "https://lnh.fr/medias/sports_teams/istres__logo__2023-2024.png";
  const ticketingUrl = settings?.ticketing_url || "https://billetterie.istreshandball.com/";

  return (
    <>
      <header
        data-testid="sport-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#160732]/95 backdrop-blur-lg shadow-[0_4px_24px_rgba(79,27,124,0.4)]"
            : "bg-[#160732]/85 backdrop-blur-lg"
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 h-[80px] flex items-center justify-between gap-2 sm:gap-3 relative">
          {/* LEFT — burger + Actualités + Business */}
          <div className="flex items-center gap-2 sm:gap-3 lg:gap-6 min-w-0">
            {/* Burger / mega menu trigger */}
            <button
              data-testid="header-burger"
              onClick={() => setMegaOpen(true)}
              className="text-white hover:text-pink-300 transition p-1"
              aria-label="Ouvrir le menu"
            >
              <Menu size={28} strokeWidth={2.5}/>
            </button>

            {/* ACTUALITÉS with animated megaphone */}
            <Link
              to="/sport/actualites"
              data-testid="header-actualites"
              className="hidden md:inline-flex items-center gap-2 text-white hover:text-pink-300 font-black uppercase tracking-wider text-xs lg:text-sm transition group"
            >
              <motion.div
                animate={{
                  rotate: [0, -15, 12, -10, 8, -5, 0],
                }}
                transition={{
                  duration: 1.4,
                  repeat: Infinity,
                  repeatDelay: 2.6,
                  ease: "easeInOut",
                }}
                style={{ originX: 0.2, originY: 0.7, color: secondary }}
              >
                <Megaphone size={18} strokeWidth={2.4}/>
              </motion.div>
              Actualités
            </Link>

            {/* ÉQUIPE PRO — accès direct à l'effectif (icône maillot pour différencier de "Mon Compte") */}
            <Link
              to="/sport/effectif"
              data-testid="header-equipe-pro"
              className="inline-flex items-center gap-2 text-white hover:text-pink-300 font-black uppercase tracking-wider text-xs lg:text-sm transition group"
              aria-label="Équipe Pro"
            >
              <span className="relative inline-flex w-9 h-9 rounded-full items-center justify-center bg-white/10 group-hover:bg-pink-500/40 border border-white/15 group-hover:border-pink-400 transition">
                <Shirt size={16} strokeWidth={2.4} className="text-pink-300 group-hover:text-white transition"/>
              </span>
              <span className="hidden sm:inline">Équipe Pro</span>
            </Link>

            {/* BUSINESS */}
            <Link
              to="/sport/partenaires"
              data-testid="header-business"
              className="hidden md:inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 font-black uppercase tracking-wider text-xs lg:text-sm transition"
            >
              <Briefcase size={16} strokeWidth={2.4}/>
              Business
            </Link>

            {/* NOTIFICATIONS BELL — mobile uniquement : à gauche pour libérer
                le cluster droit. Sur desktop, la cloche est placée entre
                "Mon Espace" et le mini panier (voir cluster droit). */}
            <NotificationsBell className="ml-auto md:hidden"/>
          </div>

          {/* CENTER LOGO — large, transparent BG, with animated shine reflection. Click → home + scroll top */}
          <Link
            to="/sport"
            data-testid="header-logo"
            onClick={() => {
              // Toujours remonter en haut, même si on est déjà sur /sport
              setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
            }}
            className="absolute left-1/2 top-2 -translate-x-1/2 group z-40 pointer-events-auto"
          >
            <div className="relative w-[88px] h-[105px] sm:w-[120px] sm:h-[144px] md:w-[150px] md:h-[180px]">
              {/* Subtle energy flow ring (no opaque bg) */}
              <motion.div
                className="absolute inset-0 rounded-[50%]"
                style={{
                  background: `conic-gradient(from 0deg, transparent 0deg, ${secondary}aa 60deg, transparent 130deg, transparent 220deg, ${secondary}77 270deg, transparent 330deg)`,
                  filter: "blur(14px)",
                  opacity: 0.55,
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
              />
              {/* Logo image — TRANSPARENT, no background frame, no drop shadow */}
              <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                <img
                  src={logo}
                  alt="IPH"
                  className="w-full h-full object-contain"
                />
                {/* Animated shine sweep */}
                <motion.span
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.85) 45%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.0) 65%)`,
                    mixBlendMode: "overlay",
                    maskImage: `url(${logo})`,
                    maskSize: "contain",
                    maskRepeat: "no-repeat",
                    maskPosition: "center",
                    WebkitMaskImage: `url(${logo})`,
                    WebkitMaskSize: "contain",
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                  }}
                  initial={{ x: "-150%" }}
                  animate={{ x: "150%" }}
                  transition={{
                    duration: 2.4,
                    repeat: Infinity,
                    repeatDelay: 3.4,
                    ease: "easeInOut",
                  }}
                />
              </div>
            </div>
          </Link>

          {/* RIGHT — Billetterie · Boutique · Mon Espace · Mini Cart */}
          <div className="flex items-center gap-1.5 sm:gap-3 lg:gap-4 ml-auto relative z-50">
            <a
              href={ticketingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-billetterie"
              className="hidden sm:inline-flex items-center gap-2 text-white hover:text-pink-300 font-black uppercase tracking-wider text-xs lg:text-sm transition"
            >
              <Ticket size={18} strokeWidth={2.4} className="text-pink-400 -rotate-12"/>
              <span className="hidden lg:inline">Billetterie</span>
            </a>

            <Link
              to="/sport/boutique"
              data-testid="header-boutique-right"
              className="hidden lg:inline-flex items-center gap-2 text-white hover:text-pink-300 font-black uppercase tracking-wider text-xs lg:text-sm transition"
            >
              <ShoppingBag size={18} strokeWidth={2.4} className="text-pink-400"/>
              <span className="hidden lg:inline">Boutique</span>
            </Link>

            {/* MON ESPACE — adaptive pill (avatar + prénom + entreprise si partenaire) */}
            <MonEspacePill/>

            {/* NOTIFICATIONS BELL — desktop only : entre Mon Espace et le mini panier */}
            <NotificationsBell className="hidden md:inline-flex"/>

            {/* Mini Cart with counter — rightmost
                (la cloche notif a été déplacée à GAUCHE du logo pour
                équilibrer le header mobile et différencier visuellement
                "panier" — droite — de "notifications" — gauche) */}
            <button
              data-testid="header-mini-cart"
              onClick={() => setDrawerOpen(true)}
              className="relative w-10 h-10 rounded-full bg-white/10 hover:bg-pink-500 hover:text-white text-white transition flex items-center justify-center"
              aria-label="Ouvrir le panier"
            >
              <ShoppingCart size={16}/>
              {count > 0 && (
                <span data-testid="mini-cart-count"
                  className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-pink-500 text-white font-black text-[10px] flex items-center justify-center shadow-lg ring-2 ring-[#160732]">
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* MEGA MENU OVERLAY */}
      <MegaMenu open={megaOpen} onClose={() => setMegaOpen(false)} primary={primary} secondary={secondary}/>
    </>
  );
}

// ---------------------------------------------------------------
// MonEspacePill — pill adaptatif :
//  - non connecté : "Mon Espace" + UserCircle2
//  - supporter : avatar (initiales) + prénom
//  - partenaire : avatar (initiales) + prénom + nom entreprise (en dessous)
// ---------------------------------------------------------------
function MonEspacePill() {
  const { customer, isAuthenticated } = useCustomerAuth();
  if (!isAuthenticated || !customer) {
    return (
      <Link
        to="/sport/mon-espace"
        data-testid="header-mon-espace"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-pink-500 backdrop-blur text-white font-black uppercase tracking-wider text-xs lg:text-sm transition border-2 border-pink-400/40 hover:border-pink-400 shadow-[0_4px_18px_rgba(236,31,124,0.35)] hover:scale-105"
      >
        <UserCircle2 size={18} strokeWidth={2.6} className="text-pink-300"/>
        <span className="hidden sm:inline">Mon Espace</span>
      </Link>
    );
  }
  const tags = ((customer || {}).tags || []).map((t) => (t || "").toLowerCase());
  const isPartner = tags.includes("partner") || customer.is_partner === true;
  const firstName = customer.first_name || customer.email?.split("@")[0] || "Compte";
  const company = customer.company_name || "";
  const initials = (firstName.charAt(0) + (customer.last_name?.charAt(0) || "")).toUpperCase() || firstName.charAt(0).toUpperCase();
  const avatarUrl = customer.avatar_url || customer.company_logo;
  const accent = isPartner ? "#f59e0b" : "#ec4899";

  return (
    <Link
      to="/sport/mon-espace"
      data-testid="header-mon-espace"
      className="inline-flex items-center gap-2 px-2 py-1 sm:pl-1 sm:pr-3 sm:py-1 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white transition border-2 hover:scale-[1.02]"
      style={{
        borderColor: `${accent}66`,
        boxShadow: `0 4px 18px ${accent}44`,
      }}
    >
      {/* Avatar circulaire */}
      <span className="relative inline-flex w-8 h-8 sm:w-9 sm:h-9 rounded-full items-center justify-center font-black text-xs sm:text-sm text-white shadow-md flex-shrink-0 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${accent}, #4F1B7C)`, border: `1.5px solid ${accent}` }}>
        {avatarUrl ? (
          <img src={avatarUrl} alt={firstName} className="w-full h-full object-cover"/>
        ) : (
          <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1rem", letterSpacing: "0.05em" }}>{initials}</span>
        )}
      </span>
      {/* Identité */}
      <span className="hidden sm:flex flex-col items-start leading-tight pr-1">
        <span className="font-black uppercase tracking-wide text-[12px] lg:text-[13px] truncate max-w-[140px]">{firstName}</span>
        {isPartner && company && (
          <span className="text-[9px] uppercase tracking-wider text-white/65 truncate max-w-[140px]">{company}</span>
        )}
        {!isPartner && (
          <span className="text-[9px] uppercase tracking-wider text-pink-300/85">Mon espace</span>
        )}
      </span>
    </Link>
  );
}
